import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Collapse,
  IconButton,
  Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ChatMessage } from './types';

interface ChatBubbleProps {
  message: ChatMessage;
}

interface FormulaRowProps {
  result: any;
  idx: number;
}

const FormulaRow: React.FC<FormulaRowProps> = ({ result, idx }) => {
  const [expanded, setExpanded] = useState(false);
  const hasDefinitions = result.plainLanguageSummary || result.businessDefinition;

  return (
    <>
      <TableRow>
        <TableCell>
          {hasDefinitions && (
            <Tooltip title={expanded ? "Collapse details" : "Expand details"}>
              <IconButton
                size="small"
                onClick={() => setExpanded(!expanded)}
                sx={{
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s',
                  mr: 1,
                  padding: '2px'
                }}
              >
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {result.label || result.label_text}
        </TableCell>
        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
          {result.dataIndex || result.data_index}
        </TableCell>
        <TableCell
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.75rem',
            maxWidth: 200,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {result.formula || ''}
        </TableCell>
      </TableRow>
      {hasDefinitions && (
        <TableRow>
          <TableCell colSpan={3} sx={{ py: 0, borderBottom: expanded ? undefined : 0 }}>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box sx={{ py: 2, px: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                {result.formulaName && (
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                    {result.formulaName}
                  </Typography>
                )}
                {result.plainLanguageSummary && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
                      Definition:
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {result.plainLanguageSummary}
                    </Typography>
                  </Box>
                )}
                {result.businessDefinition && (
                  <Box>
                    <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
                      Business Context:
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {result.businessDefinition}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const ChatBubble: React.FC<ChatBubbleProps> = ({ message }) => {
  const isUser = message.role === 'user';

  const formatTime = (date: Date) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        mb: 2
      }}
    >
      <Paper
        elevation={1}
        sx={{
          p: 2,
          maxWidth: '85%',
          bgcolor: isUser ? 'primary.main' : 'grey.100',
          color: isUser ? 'primary.contrastText' : 'text.primary',
          borderRadius: 2
        }}
      >
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {message.content}
        </Typography>

        {/* Render results table if present */}
        {!isUser && message.results && message.results.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {message.resultType === 'suggestion' ? (
                      <>
                        <TableCell>Label</TableCell>
                        <TableCell>Data Index</TableCell>
                        <TableCell>Match Score</TableCell>
                      </>
                    ) : message.resultType === 'duplicateList' ? (
                      <>
                        <TableCell>Data Index</TableCell>
                        <TableCell>Label</TableCell>
                        <TableCell>Formula Name</TableCell>
                        <TableCell>Duplicate Count</TableCell>
                      </>
                    ) : message.resultType === 'duplicate' ? (
                      <>
                        <TableCell>Source Type</TableCell>
                        <TableCell>Source Name</TableCell>
                        <TableCell>Label</TableCell>
                        <TableCell>Data Index</TableCell>
                      </>
                    ) : message.resultType === 'pivot' ? (
                      <>
                        <TableCell>Pivot Name</TableCell>
                        <TableCell>Metric Name</TableCell>
                        <TableCell>Label</TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>Label</TableCell>
                        <TableCell>Data Index</TableCell>
                        <TableCell>Formula</TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {message.results.map((result, idx) => {
                    // For default/generic result type (formulas), use expandable FormulaRow
                    if (!message.resultType || message.resultType === 'view' || message.resultType === 'formula') {
                      return <FormulaRow key={idx} result={result} idx={idx} />;
                    }

                    // For other types, use the simple TableRow
                    return (
                      <TableRow key={idx}>
                        {message.resultType === 'suggestion' ? (
                          <>
                            <TableCell>{result.label}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                              {result.dataIndex}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={result.matchScore ? `${(result.matchScore * 100).toFixed(0)}%` : 'N/A'}
                                color={result.matchScore && result.matchScore > 0.7 ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell>
                          </>
                        ) : message.resultType === 'duplicateList' ? (
                          <>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                              {result.dataIndex}
                            </TableCell>
                            <TableCell>{result.label || '-'}</TableCell>
                            <TableCell>{result.formulaName || '-'}</TableCell>
                            <TableCell>
                              <Chip
                                label={`${result.dupCount || 0}x`}
                                color="warning"
                                size="small"
                              />
                            </TableCell>
                          </>
                        ) : message.resultType === 'duplicate' ? (
                          <>
                            <TableCell>
                              <Chip
                                label={result.sourceType || 'Unknown'}
                                color={result.sourceType === 'View' ? 'primary' : 'secondary'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{result.sourceName}</TableCell>
                            <TableCell>{result.label}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                              {result.dataIndex}
                            </TableCell>
                          </>
                        ) : message.resultType === 'pivot' ? (
                          <>
                            <TableCell>{result.pivotName}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                              {result.metricName}
                            </TableCell>
                            <TableCell>{result.formulaLabel || result.label}</TableCell>
                          </>
                        ) : null}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Showing {message.results.length} results
            </Typography>
          </Box>
        )}

        {/* Reasoning */}
        {!isUser && message.reasoning && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
            {message.reasoning}
          </Typography>
        )}

        {/* Timestamp */}
        <Typography
          variant="caption"
          sx={{
            mt: 1,
            display: 'block',
            opacity: 0.7,
            fontSize: '0.7rem'
          }}
        >
          {formatTime(message.timestamp)}
        </Typography>
      </Paper>
    </Box>
  );
};

export default ChatBubble;
